import { useFrozenMode } from '../../hooks/useFrozenMode'
import useCRMStore from '../../store/useCRMStore'

/**
 * Chip visual del estado frozen del grid + botón "Descongelar ahora" + toggle "Hasta 00:00".
 *
 * Renderiza:
 *  - Si frozen NO activo: solo el toggle "Mantener orden todo el día" (opt-in para la
 *    proxima sesion frozen).
 *  - Si frozen activo: chip ambar con countdown + boton descongelar + toggle "Hasta 00:00".
 *  - Estilo se torna naranja cuando quedan ≤30s (aviso preventivo de descongele).
 *  - Etiqueta cambia segun fullDayMode: "Modo día" vs "Orden bloqueado".
 *  - Dark mode variants.
 *
 * Side effect (vía useFrozenMode): re-renderiza cada 1s mientras frozen.active.
 */
export default function FrozenStatusChip() {
  const { active, secondsLeft, countdownText, fullDayMode, exitFrozen } = useFrozenMode()
  const setFullDayMode = useCRMStore(s => s.setFullDayMode)

  // Estilo se torna naranja cuando quedan <=30s como aviso preventivo de descongele.
  const isLow = secondsLeft <= 30

  if (!active) {
    // Mostrar solo el toggle: el agente puede activar el opt-in en frio para la
    // proxima vez que se congele la grilla.
    return (
      <div className="flex items-center gap-2 flex-shrink-0">
        <label
          className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none"
          title="Si está activo, al congelar el orden se mantiene hasta las 00:00 (Chile) en vez de los 3 min por defecto."
        >
          <input
            type="checkbox"
            checked={fullDayMode}
            onChange={(e) => setFullDayMode(e.target.checked)}
            className="rounded border-gray-300 dark:border-slate-600 text-amber-500 focus:ring-amber-400"
          />
          <span>Mantener orden todo el día</span>
        </label>
      </div>
    )
  }

  const chipClass = isLow
    ? 'bg-orange-100 dark:bg-orange-900/40 border-orange-400 dark:border-orange-600 text-orange-900 dark:text-orange-100'
    : 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 dark:border-amber-700 text-amber-900 dark:text-amber-200'

  const label = fullDayMode ? 'Modo día' : 'Orden bloqueado'
  const chipTitle = fullDayMode
    ? 'Tu orden se mantiene hasta las 00:00 (Chile). Editar prospectos extiende el contador.'
    : 'Tu orden está bloqueado. Descongela en este tiempo o manualmente. Editar prospectos resetea el contador.'

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${chipClass} transition-colors`}
        title={chipTitle}
      >
        <span aria-hidden="true">{'\u{1F512}'}</span>
        <span>{label}</span>
        <span className="font-mono font-semibold tabular-nums">{countdownText}</span>
      </span>
      <button
        onClick={exitFrozen}
        className="px-2.5 py-1 bg-white dark:bg-surface-dark-alt border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition shadow-sm"
        title="Descongelar el orden de la grilla y aplicar el orden actualizado"
      >
        Descongelar ahora
      </button>
      <label
        className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none ml-1"
        title="Cuando está marcado, el orden se mantiene hasta las 00:00 (Chile)."
      >
        <input
          type="checkbox"
          checked={fullDayMode}
          onChange={(e) => setFullDayMode(e.target.checked)}
          className="rounded border-gray-300 dark:border-slate-600 text-amber-500 focus:ring-amber-400"
        />
        <span>Hasta 00:00</span>
      </label>
    </div>
  )
}
